import { useEffect, useState } from "react";

import { initZusParams, useZusParams } from "..";
import { TRANSLATE_PREFIX } from "../../..";
import { useDatePicker } from "../../../../../../components/DatePicker";
import { useAssets, useChains } from "../../../../../../components/FetchConfig";
import GeneralBtn from "../../../../../../components/GeneralBtn";
import MpTextField from "../../../../../../components/MpTextField";
import {
  Box,
  SelectChangeEvent,
} from "../../../../../../components/MuiGenerals";
import { useAlerting } from "../../../../../../hooks";
import { genField } from "../../../../../../utils/HelperComp";
import { customSx } from "../../../../../../utils/styling";
import useTabTranslation from "src/hooks/useTabTranslation";
import { TAB_TRANSLTE_PREFIX } from "../constants";
import { FilterFields } from "../types/FilterFields";
import { ApiParams } from "../types/ApiParams";
import ChainNameSingleSelection from "src/components/ContractSweep/ChainNameSingleSelection";
import CurrencySingleSelection from "src/components/ContractSweep/CurrencySingleSelection";
import RiskTypeSingleSelection from "src/components/RiskTypeSingleSelection";

const initFields: FilterFields = {
  chainName: "",
  currency: "",
  recipientAddress: "",
  riskType: "",
};

export function Filter() {
  const [fields, setFields] = useState<FilterFields>(initFields);
  const { t, tc, translate } = useTabTranslation({
    featurePrefix: TRANSLATE_PREFIX,
    tabPrefix: TAB_TRANSLTE_PREFIX,
  });
  const zusParams = useZusParams();
  const { alerting } = useAlerting();
  const { selectChainByPieces } = useChains();
  const { selectAssetNamesByChain } = useAssets();
  const DateObj = {
    ActiveStartTime: useDatePicker(),
    ActiveEndTime: useDatePicker(),
  };

  const getParams: () => ApiParams = () => {
    const {
      chainName,
      currency: assetName,
      recipientAddress: walletAddress,
      ...rest
    } = fields;

    const { chain_type, chain_id } = selectChainByPieces({
      chain_name: fields.chainName,
    });
    const { ActiveStartTime, ActiveEndTime } = DateObj;
    const { start: enabledTimeFrom, end: enabledTimeTo } = ActiveStartTime;
    const { start: disabledTimeFrom, end: disabledTimeTo } = ActiveEndTime;

    return {
      ...initZusParams,
      chainId: chain_id,
      chainType: chain_type,
      assetName,
      walletAddress,
      enabledTimeFrom,
      enabledTimeTo,
      disabledTimeFrom,
      disabledTimeTo,
      ...rest,
    };
  };

  const apiParams = getParams();

  const onChange =
    (field: keyof FilterFields) =>
    (
      e:
        | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
        | SelectChangeEvent<string>,
    ) => {
      const value = e.target.value;

      setFields((fields) => ({ ...fields, [field]: value }));
    };
  const onClear = (field: keyof FilterFields) => () => {
    setFields((fields) => ({ ...fields, [field]: "" }));
  };

  const onSearch = () => {
    zusParams.setBody({ ...apiParams, page: 0 });
    zusParams.refetch();
  };

  const onReset = () => {
    setFields(initFields);
    DateObj.ActiveStartTime.clearDate();
    DateObj.ActiveEndTime.clearDate();
  };
  const allAssetNames = selectAssetNamesByChain({
    chain_name: fields.chainName,
  });
  useEffect(
    () => setFields((f) => ({ ...f, currency: "" })),
    [fields.chainName],
  );

  const F = genField({ t }, [
    ["activeStartTime", <DateObj.ActiveStartTime.Picker type="dateTime" />],
    ["activeEndTime", <DateObj.ActiveEndTime.Picker type="dateTime" />],
    [
      "chainName",
      <ChainNameSingleSelection
        fieldName={t("chainName")}
        value={fields.chainName}
        onChange={onChange("chainName")}
        onClear={onClear("chainName")}
      />,
    ],
    [
      "currency",
      <CurrencySingleSelection
        fieldName={t("currency")}
        value={fields.currency}
        onChange={onChange("currency")}
        onClear={onClear("currency")}
        chainName={fields.chainName}
      />,
    ],

    [
      "recipientAddress",
      <MpTextField
        label={tc("phInputField", { fieldName: t("recipientAddress") })}
        onChange={onChange("recipientAddress")}
        value={fields.recipientAddress}
      />,
    ],
    [
      "riskType",
      <RiskTypeSingleSelection
        onChange={onChange("riskType")}
        value={fields.riskType}
        onClearSelect={onClear("riskType")}
      />,
    ],
  ]);
  return (
    <>
      <Box sx={customSx.gridFilter} className="gridFilter">
        {F.activeStartTime}
        {F.activeEndTime}
        {F.chainName}
        {F.currency}
        {F.recipientAddress}
        {F.riskType}
      </Box>
      <Box sx={customSx.filterB}>
        <GeneralBtn label="search" onClick={onSearch} />
        <GeneralBtn label="reset" onClick={onReset} />
      </Box>
    </>
  );
}
